import { isPlatformBrowser, Location } from '@angular/common';
import type { OnDestroy } from '@angular/core';
import { Inject, Injectable, InjectionToken, PLATFORM_ID } from '@angular/core';
import { ScriptsService, GoogleAnalyticsService, DataLayerEventTypes } from '@candyland/angular/shared/util-nmo';
import { oAuthClientId } from '@candyland/generic/shared/model-showcase-feed/ea';
import { OAuthService } from 'angular-oauth2-oidc';
import { HttpClient } from '@angular/common/http';
import type { Observable, Subscription } from 'rxjs';
import type { AccountCreateResponseModel } from '@candyland/generic/shared/tt-com-anmeldung/models';
// eslint-disable-next-line @nx/enforce-module-boundaries -- TODO HttpStatus should be available to shared scope
import { HttpStatus400 } from '@candyland/generic/tt-com-anmeldung/models';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { ErrorModalComponent } from './helper/error-modal.component';
import { Router } from '@angular/router';

export const EXTERNAL_AUTH_PROVIDER_ENDPOINT = new InjectionToken('external-auth-provider endpoint');

@Injectable()
export class ShowcaseService implements OnDestroy {
  private calledInit = false;
  private subscriptions: Subscription[] = [];

  constructor(
    private scriptsService: ScriptsService,
    private oAuthService: OAuthService,
    private location: Location,
    private httpClient: HttpClient,
    private bottomSheet: MatBottomSheet,
    private gaService: GoogleAnalyticsService,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(EXTERNAL_AUTH_PROVIDER_ENDPOINT) private externalAuthProviderEndpoint: string
  ) {}

  ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }

  init(params: InitParams): void {
    if (this.calledInit || !isPlatformBrowser(this.platformId)) {
      return;
    }

    this.calledInit = true;

    // Add "async: false" to guarantee order. EA Support was not clear, if more flexible execution orders would be ok.
    this.scriptsService.addScript({ src: 'https://news.google.com/swg/js/v1/swg-gaa.js' }, { async: false });
    this.scriptsService.addScript(
      {
        'subscriptions-control': 'manual',
        src: 'https://news.google.com/swg/js/v1/swg.js',
      },
      { async: false }
    );
    this.scriptsService.addScript(
      { src: 'https://accounts.google.com/gsi/client' },
      { async: false, onload: () => this.onLoad.call(this, params) }
    );
  }

  private onLoad(params: InitParams) {
    const eaInitParams = {
      googleApiClientId: oAuthClientId,
      userState: params.eaInit.userState,
      showcaseEntitlement: params.eaInit.showcaseEntitlement, // https://developers.google.com/news/subscribe/extended-access/integration-steps/web-implementation?feed=server-side#client-side-consumption-of-entitlements
      registerUserPromise: new Promise(() => {
        // Google wants to have a promise, even though it's never settled...
        // Get the information for the user who has just registered.
        (window as any).GaaMetering.getGaaUserPromise().then((gaaUser) => {
          this.subscriptions.push(
            this.registerUser(gaaUser.credential).subscribe({
              next: (val) =>
                this.login({ token: val.oneTimeLoginToken, email: unsafeJwtClaims(gaaUser.credential).email }),
              error: (err) => {
                const errorKind =
                  err.status === 400 && err.error.number === HttpStatus400.EmailAddressAlreadyOccupied
                    ? 'emailAlreadyRegistered'
                    : 'other';

                this.bottomSheet.open(ErrorModalComponent, {
                  data: { errorKind },
                  closeOnNavigation: true,
                });
              },
            })
          );
        });
      }),
      handleLoginPromise: new Promise(() => {
        (window as any).GaaMetering.getLoginPromise().then(() => this.login());
      }),
      publisherEntitlementPromise: Promise.resolve(params.eaInit.userState), // Google might just need every property set...
      unlockArticle: () => console.log('Google called unlockArticle... NoOp for us, because we handle it on server'), // Google might just need every property set...

      // This might only work like this, because we are not initializing EA at all,
      // if no access is granted by either Google or us.
      // If that ever changes, Google will probably also just call this, when they are not granting access...
      // https://developers.google.com/news/subscribe/extended-access/integration-steps/web-implementation?feed=server-side#initialize-the-extended-access-library
      showPaywall: (): string => (window.location.href = `https://www.tt.com/abo-aktion/tt-com-plus-monatsabo`),
      allowedReferrers: [
        'oauth.tt.com',
        'hydra.stage.oauth.nmo.at',
        'localhost',
        '',
        'tt-com-www.stage.tt.nmo.at',
        'www.tt.com',
      ], // Seems to work like this with the current setup, but again, the docs just don't tell...
    };

    (window as any).GaaMetering.init(eaInitParams);

    if (params.eaInit.showcaseEntitlement) {
      this.gaService.pushEvent({
        event: DataLayerEventTypes.ArticleUnlockedByGoogleEA,
        articleId: params.articleId,
        ngRouterUrl: this.router.url,
        relativeUrl: this.location.path(),
      });
    }
  }

  private registerUser(token: string): Observable<AccountCreateResponseModel> {
    const url = this.externalAuthProviderEndpoint + '/google/create_user_by_auth_token';
    return this.httpClient.post<AccountCreateResponseModel>(url, {
      auth_token: token,
      create_one_time_login_token: true,
    });
  }

  private login(oneTimeLogin?: { token: string; email: string }): void {
    const state = {
      path: this.location.path(true) || '/',
    };
    this.oAuthService.initCodeFlow(
      JSON.stringify(state),
      oneTimeLogin ? { nmo_ott: oneTimeLogin.token, nmo_email: oneTimeLogin.email } : {}
    );
  }
}

function unsafeJwtClaims(jwt: string): Record<string, any> {
  const base64Url = jwt.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join('')
  );

  return JSON.parse(jsonPayload);
}

type GrantState =
  | {
      granted: false;
    }
  | {
      granted: true;
      grantedReason: string;
    };

/* eslint-disable @typescript-eslint/ban-types */
// Type {} is only ok in intersections, where it behaves what it looks like: an empty object
// https://github.com/typescript-eslint/typescript-eslint/issues/2063#issuecomment-675156492
export type UserState = GrantState &
  ({} | { id: string; registrationTimestamp: number }) & { subscriptionTimestamp?: number };
/* eslint-enable */

export interface EAInitParams {
  userState: UserState;
  showcaseEntitlement?: string;
}

export interface InitParams {
  eaInit: EAInitParams;
  articleId: number;
}
